import type { Sequence } from "#public"

/**
 * Maps a function over one or more sequences, yielding transformed values.
 * Processes multiple sequences sequentially (not in parallel).
 * Generator function for lazy evaluation.
 *
 * @param func - Function to apply to each item (receives item and index)
 * @param arrays - One or more sequences to map over
 * @returns Generator yielding transformed values
 *
 * @example
 * ```ts
 * import { Map } from "@pdfking/tfunc"
 *
 * Array.from(Map(x => x * 2, [1, 2, 3])) // [2, 4, 6]
 * Array.from(Map((x, i) => `${i}: ${x}`, ["a", "b", "c"])) // ["0: a", "1: b", "2: c"]
 *
 * // Multiple sequences processed sequentially
 * Array.from(Map(x => x * 2, [1, 2], [3, 4])) // [2, 4, 6, 8]
 * ```
 */
export const Map = function* <T, N>(
  func: (item: T, index: number) => N,
  ...arrays: Sequence<T>[]
): Generator<N, void, unknown> {
  for (const array of arrays) {
    let index = 0
    for (const item of array) {
      yield func(item, index++)
    }
  }
}
